import {
    BellOutlined,
    CloseOutlined,
    DeleteOutlined,
    FileOutlined,
    MoreOutlined,
    PushpinOutlined,
    SendOutlined
} from '@ant-design/icons'
import { useLastAppStateStore } from '@app/providers/router/model/store/last-app-state-store'
import { useChannels } from '@entities/channel'
import { MessageView } from '@entities/post'
import { checkDraftPost, savePayload } from '@entities/post/api'
import { basePostType } from '@shared/lib/constants/constants'
import { randomId } from '@shared/lib/randomId/randomId'
import { CustomTag } from '@shared/ui/custom-tag/custom-tag'
import { IconSwitch } from '@shared/ui/icon-switch/icon-switch'
import type { FormProps } from 'antd'
import { Button, Col, Divider, Dropdown, Flex, Form, Row, Select, Space, theme, Typography } from 'antd'
import { Dialog, Input, Popup } from 'antd-mobile'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { useShallow } from 'zustand/react/shallow'
import { usePosts } from '../../model/hooks/use-posts'
import { MessageType, type Message, type Post, type PostState } from '../../model/types'
import * as styles from './edit-post-mobile-modal.module.scss'

const { Text } = Typography

interface EditPostModalProps {
    open: boolean
    editPostData: Post
    sessionId: string | null
    onClose: () => void
}

type FieldType = {
    title: string
    state: PostState
    channels: number[]
    publishDate: Dayjs
    publishTime: Dayjs
    deleteDate: Dayjs
    notify: boolean
    isPinned: boolean
    messages: Message[]
    postType: {
        value: number
    }
}

const BOT_USERNAME = process.env.BOT_USERNAME || ''

export const EditPostMobileModal = ({ open, onClose, sessionId, editPostData }: EditPostModalProps) => {
    const {
        actions: { editPost, deletePost, publishPostNow },
        postTypes
    } = usePosts()

    const { channels } = useChannels()

    const {
        token: { colorPrimary, colorWhite }
    } = theme.useToken()

    const [form] = Form.useForm<FieldType>()

    const onFinish: FormProps<FieldType>['onFinish'] = useCallback(
        async (values: FieldType) => {
            const dateStr = values.publishDate // '2025-06-13'
            const timeStr = values.publishTime // '14:30'

            const [hour, minute] = timeStr.split(':').map(Number)

            const publishDate = dayjs(dateStr).hour(hour).minute(minute).second(0)

            editPost({
                ...values,
                id: editPostData.id,
                publishDate: publishDate.toDate()
            })

            onClose()
        },
        [editPost, editPostData.id, onClose]
    )

    const onSubmit = useCallback(
        (state?: Partial<FieldType>) => {
            if (state) {
                form.setFieldsValue({
                    ...state
                })
            }

            form.submit()
        },
        [form]
    )

    const {
        token: { colorLink }
    } = theme.useToken()

    const { setState } = useLastAppStateStore(useShallow(({ setState }) => ({ setState })))

    const onEditPostUsingBot = useCallback(async () => {
        const tg = (window as any).Telegram?.WebApp

        tg.showConfirm('Для редактирования содержания поста, необходимо перейти в бот', async (ok?: boolean) => {
            if (!ok) return

            const sessionId = randomId()

            setState({ page: location.pathname, action: 'edit-post', sessionId: sessionId, postId: editPostData.id })

            const payloadId = await savePayload({
                action: 'edit_post',
                session_id: sessionId,
                post_id: editPostData.id,
                platform: 'mobile'
            })

            const url = `https://t.me/${BOT_USERNAME}?start=payload_${payloadId}`

            tg.openTelegramLink(url)
            tg.close()
        })
    }, [editPostData.id, setState])

    useEffect(() => {
        if (!sessionId) return
        checkDraftPost(sessionId).then((postData) => {
            if (postData) {
                form.setFieldsValue({
                    messages: [
                        {
                            ...postData.post,
                            id: Date.now(),
                            file: postData.post.file.map((file) => {
                                const item = {
                                    ...file,
                                    name: file.response.file_name,
                                    type: file.response.file_type,
                                    url: file.response.file_url
                                }
                                if (
                                    file.response.file_type !== MessageType.AUDIO &&
                                    file.response.file_type !== MessageType.VOICE &&
                                    file.response.file_type !== MessageType.DOCUMENT
                                ) {
                                    item.thumbUrl = file.response?.preview_url || file.response.file_url
                                }
                                return item
                            })
                        }
                    ]
                })
            }
        })
    }, [form, sessionId])

    const moreActionsItems = useMemo(
        () => [
            {
                key: '1',
                label: (
                    <Space style={{ width: '100%', color: colorLink }}>
                        <FileOutlined />В черновики
                    </Space>
                ),
                onClick: () => onSubmit()
            },
            {
                key: '3',
                label: (
                    <Space style={{ color: colorLink, width: '100%' }}>
                        <SendOutlined /> Опубликовать сейчас
                    </Space>
                ),
                onClick: () =>
                    Dialog.confirm({
                        content: 'Вы уверены что хотите опубликовать пост?',
                        cancelText: 'Отмена',
                        confirmText: 'Опубликовать',
                        onConfirm: () => publishPostNow(editPostData.id)
                    })
            },
            {
                key: '4',
                label: (
                    <Space style={{ width: '100%' }}>
                        <DeleteOutlined />
                        Удалить
                    </Space>
                ),
                danger: true,
                onClick: () =>
                    Dialog.confirm({
                        content: 'Вы уверены что хотите удалить пост?',
                        cancelText: 'Отмена',
                        confirmText: 'Удалить',
                        onConfirm: () => deletePost(editPostData.id)
                    })
            }
        ],
        [colorLink, deletePost, editPostData.id, onEditPostUsingBot, onSubmit, publishPostNow]
    )

    useEffect(() => {
        form.resetFields()
    }, [form, open])

    const channelsToDisplay = useMemo(() => channels?.map((c) => ({ label: c.name, value: c.id })), [channels])

    const navigate = useNavigate()

    const postType =
        editPostData.postType?.name && editPostData.postType?.value
            ? {
                  label: editPostData.postType?.name,
                  value: editPostData.postType?.value
              }
            : undefined

    return (
        <Popup visible={open} onMaskClick={onClose} onClose={onClose} bodyStyle={{ background: '#F0F3F5' }}>
            <Form
                form={form}
                name="add-post"
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                onFinish={onFinish}
                initialValues={{
                    ...editPostData,
                    postType,
                    publishDate: editPostData?.publishDate
                        ? dayjs(editPostData.publishDate).format('YYYY-MM-DD')
                        : null,
                    publishTime: editPostData?.publishDate
                        ? dayjs(editPostData.publishDate).format('HH:mm')
                        : dayjs().format('HH:mm'),
                    messages: sessionId ? [] : editPostData?.messages
                }}
                style={{ padding: '20px 16px', maxHeight: '100vh', overflow: 'auto' }}
            >
                <Row gutter={[10, 10]}>
                    <Col span={24}>
                        <div style={{ padding: '16px', background: '#FEFEFF', borderRadius: '12px' }}>
                            <Row gutter={[15, 15]}>
                                <Col span={24}>
                                    <Flex gap={6}>
                                        <CustomTag color={basePostType.color}>{basePostType.name}</CustomTag>
                                        <Form.Item<FieldType>
                                            name="postType"
                                            required={false}
                                            style={{ marginBottom: 0 }}
                                        >
                                            <Select
                                                placeholder="Выберите тип"
                                                labelInValue
                                                optionLabelProp="label"
                                                popupMatchSelectWidth={false}
                                                popupRender={(menu) => (
                                                    <>
                                                        {menu}
                                                        <Divider style={{ margin: '8px 0' }} />
                                                        <div style={{ padding: '8px', textAlign: 'center' }}>
                                                            <Button
                                                                type="primary"
                                                                onClick={() => navigate('/settings')}
                                                            >
                                                                + Новый тип
                                                            </Button>
                                                        </div>
                                                    </>
                                                )}
                                            >
                                                {postTypes?.map((item) => (
                                                    <Select.Option
                                                        style={{ textAlign: 'center' }}
                                                        key={item.id}
                                                        value={item.id}
                                                        label={item.name}
                                                    >
                                                        <CustomTag color={item.color}>{item.name}</CustomTag>
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Flex>
                                </Col>
                                <Col span={24}>
                                    <Form.Item<FieldType>
                                        name="channels"
                                        required={false}
                                        rules={[{ required: true, message: 'Обязательное поле' }]}
                                        style={{ marginBottom: 0 }}
                                    >
                                        <Select
                                            showSearch
                                            mode="multiple"
                                            optionFilterProp="label"
                                            style={{ width: '100%' }}
                                            options={channelsToDisplay}
                                            size="large"
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </div>
                    </Col>
                    <Col span={24}>
                        <div style={{ padding: '16px', background: '#FEFEFF', borderRadius: '12px' }}>
                            <Text style={{ whiteSpace: 'nowrap' }}>Опубликовать</Text>
                            <Flex gap={8} align="center" style={{ marginTop: '10px' }}>
                                <div className={styles.inputCard}>
                                    <Form.Item<FieldType>
                                        name="publishDate"
                                        required={false}
                                        rules={[{ required: true, message: 'Обязательное поле' }]}
                                        style={{ marginBottom: 0, marginLeft: 'auto' }}
                                    >
                                        <Input type="date" placeholder="Дата публикации" style={{ width: '120px' }} />
                                    </Form.Item>
                                </div>
                                <div className={styles.inputCard}>
                                    <Form.Item<FieldType>
                                        name="publishTime"
                                        required={false}
                                        rules={[{ required: true, message: 'Обязательное поле' }]}
                                        style={{ marginBottom: 0 }}
                                    >
                                        <Input type="time" placeholder="Время публикации" style={{ width: '82px' }} />
                                    </Form.Item>
                                </div>
                            </Flex>
                        </div>
                    </Col>
                    <Col span={24}>
                        <div style={{ padding: '16px', background: '#FEFEFF', borderRadius: '12px' }}>
                            <Flex align="center">
                                <Text style={{ whiteSpace: 'nowrap' }}>Закреп/Уведомления</Text>
                                <Form.Item<FieldType>
                                    name="isPinned"
                                    required={false}
                                    style={{ marginLeft: 'auto', marginBottom: 0 }}
                                >
                                    <IconSwitch text={'Закрепить'}>
                                        <PushpinOutlined style={{ fontSize: '26px', color: colorPrimary }} />
                                    </IconSwitch>
                                </Form.Item>
                                <Form.Item<FieldType> name="notify" required={false} style={{ marginBottom: 0 }}>
                                    <IconSwitch text={'Отправить со звуком'}>
                                        <BellOutlined style={{ fontSize: '26px', color: colorPrimary }} />
                                    </IconSwitch>
                                </Form.Item>
                            </Flex>
                        </div>
                    </Col>
                    <Col span={24}>
                        <div style={{ padding: '16px', background: '#FEFEFF', borderRadius: '12px' }}>
                            {form
                                .getFieldsValue()
                                .messages?.map((item) => <MessageView message={item} key={item.id} />)}
                            <Button
                                type="primary"
                                size="large"
                                style={{ width: '100%', marginTop: '10px' }}
                                onClick={onEditPostUsingBot}
                            >
                                Редактировать
                            </Button>
                        </div>
                    </Col>

                    <Col span={24}>
                        <Flex gap={6}>
                            <Button type="primary" onClick={onClose} size="large">
                                <CloseOutlined />
                            </Button>
                            <Button
                                type="primary"
                                style={{ flex: 1 }}
                                onClick={() => onSubmit({ state: 'planned' })}
                                size="large"
                            >
                                Запланировать
                            </Button>
                            <Dropdown
                                key="more"
                                trigger={['click']}
                                menu={{
                                    items: moreActionsItems
                                }}
                            >
                                <Button type="primary" style={{ verticalAlign: 'bottom' }} size="large">
                                    <MoreOutlined style={{ color: colorWhite }} />
                                </Button>
                            </Dropdown>
                        </Flex>
                    </Col>
                </Row>

                <Form.Item<FieldType> name="state" hidden>
                    <Input />
                </Form.Item>
                <Form.Item name="messages" hidden />
                <Form.Item<FieldType> label="Введите название поста" name="title" hidden initialValue={'Title'}>
                    <Input />
                </Form.Item>
            </Form>
        </Popup>
    )
}
